import React from 'react';
import ReactMarkdown from 'react-markdown';
import { MarkdownTable } from './markdown-table';
import { PositioningMatrix } from './positioning-matrix';
import { AdvantagesTable } from './advantages-table';

interface EnhancedMarkdownProps {
  content: string;
  className?: string;
}

// Custom components for ReactMarkdown
const MarkdownComponents: any = {
  h1: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <h1 {...props} className="text-3xl font-bold mb-6 mt-8 text-gray-900 border-b pb-2 text-left" />
  ),
  h2: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <h2 {...props} className="text-2xl font-semibold mb-4 mt-6 text-gray-800 text-left" />
  ),
  h3: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <h3 {...props} className="text-xl font-semibold mb-3 mt-4 text-gray-700 text-left" />
  ),
  h4: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <h4 {...props} className="text-lg font-medium mb-2 mt-3 text-gray-700 text-left" />
  ),
  p: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <p {...props} className="mb-4 text-gray-700 leading-relaxed text-left break-words" />
  ),
  ul: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <ul {...props} className="mb-4 ml-6 list-disc space-y-2 text-left" />
  ),
  ol: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <ol {...props} className="mb-4 ml-6 list-decimal space-y-2 text-left" />
  ),
  li: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <li {...props} className="text-gray-700 leading-relaxed text-left break-words" />
  ),
  strong: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <strong {...props} className="font-semibold text-gray-900" />
  ),
  em: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <em {...props} className="italic text-gray-700" />
  ),
  blockquote: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <blockquote {...props} className="border-l-4 border-blue-500 pl-4 my-4 italic text-gray-600" />
  ),
  code: ({ node, inline, ...props }: { [x: string]: any; node: any; inline?: boolean }) => {
    if (inline) {
      return <code {...props} className="bg-gray-100 rounded px-1 py-0.5 font-mono text-sm text-gray-800" />;
    }
    return <code {...props} className="block bg-gray-100 rounded p-4 font-mono text-sm text-gray-800 overflow-x-auto" />;
  },
  pre: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <pre {...props} className="mb-4 overflow-x-auto" />
  ),
  a: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <a 
      {...props} 
      className="text-blue-600 hover:text-blue-800 underline cursor-pointer transition-colors"
      target="_blank" 
      rel="noopener noreferrer"
    />
  ),
  hr: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <hr {...props} className="my-6 border-t border-gray-300" />
  ),
  // Default table rendering (will be overridden by our custom logic)
  table: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <div className="overflow-x-auto my-6">
      <table {...props} className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden" />
    </div>
  ),
  thead: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <thead {...props} className="bg-gradient-to-r from-gray-50 to-gray-100" />
  ),
  tbody: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <tbody {...props} className="bg-white divide-y divide-gray-200" />
  ),
  tr: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <tr {...props} className="hover:bg-gray-50 transition-colors" />
  ),
  th: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <th {...props} className="px-6 py-4 text-left text-sm font-semibold text-gray-900 uppercase tracking-wider" />
  ),
  td: ({ node, ...props }: { [x: string]: any; node: any }) => (
    <td {...props} className="px-6 py-4 text-sm text-gray-700 whitespace-normal" />
  )
};

export const EnhancedMarkdown: React.FC<EnhancedMarkdownProps> = ({ content, className }) => {
  // Pre-process content to handle special cases
  const processContent = (text: string): React.ReactNode[] => {
    const elements: React.ReactNode[] = [];
    let keyCounter = 0;
    
    // More flexible table detection - look for multiple line table patterns
    const findTables = (text: string) => {
      const tables: { match: string; isPositioningMatrix: boolean; isAdvantagesTable: boolean; startIndex: number; endIndex: number }[] = [];
      
      // Split text into lines and look for table patterns
      const lines = text.split('\n');
      let i = 0;
      
      while (i < lines.length) {
        const line = lines[i];
        
        // Check if this line starts a table (contains | characters)
        if (line.includes('|') && line.trim().length > 0) {
          let tableStartIndex = text.indexOf(line, i > 0 ? text.indexOf(lines[i-1]) + lines[i-1].length : 0);
          let tableLines = [line];
          let j = i + 1;
          
          // Look ahead for more table lines
          while (j < lines.length && (lines[j].includes('|') || lines[j].trim() === '')) {
            if (lines[j].includes('|')) {
              tableLines.push(lines[j]);
            }
            j++;
          }
          
          // Check if we have a valid table (at least 2 lines with |, and one should be a separator)
          const hasHeader = tableLines.length >= 2;
          const hasSeparator = tableLines.some(line => 
            line.includes('|') && 
            line.includes('-') && 
            line.split('|').every(cell => cell.trim() === '' || /^[\s-:]+$/.test(cell.trim()))
          );
          
          if (hasHeader && (hasSeparator || tableLines.length >= 2)) {
            const tableContent = tableLines.join('\n');
            const lowerContent = tableContent.toLowerCase();
            const precedingText = text.substring(Math.max(0, tableStartIndex - 200), tableStartIndex).toLowerCase();
            
            const isPositioningMatrix = 
              lowerContent.includes('positioning matrix') ||
              precedingText.includes('positioning matrix') ||
              (lowerContent.includes('high specialization') && lowerContent.includes('low specialization')) ||
              (lowerContent.includes('slide-first') && lowerContent.includes('speech-first'));
            
            const isAdvantagesTable = 
              lowerContent.includes('advantages') && lowerContent.includes('disadvantages');
            
            // Find the actual end position of the table
            const lastTableLine = tableLines[tableLines.length - 1];
            const tableEndIndex = text.indexOf(lastTableLine, tableStartIndex) + lastTableLine.length;
            
            tables.push({
              match: tableContent,
              isPositioningMatrix,
              isAdvantagesTable,
              startIndex: tableStartIndex,
              endIndex: tableEndIndex
            });
            
            // Skip to after this table
            i = j;
          } else {
            i++;
          }
        } else {
          i++;
        }
      }
      
      return tables;
    };
    
    const tables = findTables(text);
    
    // Process the content, replacing tables with placeholders
    let lastIndex = 0;
    tables.forEach((table, index) => {
      // Add markdown content before the table
      const beforeTable = text.substring(lastIndex, table.startIndex);
      if (beforeTable.trim()) {
        elements.push(
          <ReactMarkdown key={`md-${keyCounter++}`} components={MarkdownComponents}>
            {beforeTable}
          </ReactMarkdown>
        );
      }
      
      // Add the table or matrix component
      if (table.isPositioningMatrix) {
        elements.push(
          <PositioningMatrix key={`matrix-${keyCounter++}`} companies={[]} />
        );
      } else if (table.isAdvantagesTable) {
        elements.push(
          <AdvantagesTable key={`advantages-${keyCounter++}`} content={table.match} />
        );
      } else {
        elements.push(
          <MarkdownTable key={`table-${keyCounter++}`} content={table.match} />
        );
      }
      
      lastIndex = table.endIndex;
    });
    
    // Add any remaining content after the last table
    const remainingContent = text.substring(lastIndex);
    if (remainingContent.trim()) {
      // Check for positioning matrix in text format (not table)
      const lowerRemaining = remainingContent.toLowerCase();
      if (lowerRemaining.includes('positioning matrix') && !remainingContent.includes('|')) {
        // Look for the entire positioning matrix section including title and remove it
        // This regex will match the title and any content that follows until a new section or end
        const matrixSectionMatch = remainingContent.match(/(#{1,6}\s*)?.*?positioning matrix.*?(?=\n#{1,6}\s|$)/is);
        if (matrixSectionMatch) {
          const beforeMatrix = remainingContent.substring(0, matrixSectionMatch.index);
          const afterMatrix = remainingContent.substring(matrixSectionMatch.index! + matrixSectionMatch[0].length);
          
          if (beforeMatrix.trim()) {
            elements.push(
              <ReactMarkdown key={`md-${keyCounter++}`} components={MarkdownComponents}>
                {beforeMatrix}
              </ReactMarkdown>
            );
          }
          
          // Skip the positioning matrix section entirely since we have no data
          
          if (afterMatrix.trim()) {
            elements.push(
              <ReactMarkdown key={`md-${keyCounter++}`} components={MarkdownComponents}>
                {afterMatrix}
              </ReactMarkdown>
            );
          }
        } else {
          elements.push(
            <ReactMarkdown key={`md-${keyCounter++}`} components={MarkdownComponents}>
              {remainingContent}
            </ReactMarkdown>
          );
        }
      } else {
        elements.push(
          <ReactMarkdown key={`md-${keyCounter++}`} components={MarkdownComponents}>
            {remainingContent}
          </ReactMarkdown>
        );
      }
    }
    
    // If no elements were processed (no tables found), render the entire content as markdown
    if (elements.length === 0) {
      elements.push(
        <ReactMarkdown key={`md-fallback`} components={MarkdownComponents}>
          {text}
        </ReactMarkdown>
      );
    }
    
    return elements;
  };
  
  return (
    <div className={`${className} w-full break-words`}>
      {processContent(content)}
    </div>
  );
};